'use client';

import { usePathname as useFullPathname } from 'next/navigation';
import { ReactNode, createContext, useContext, useMemo } from 'react';

import { Locales } from '@monoimin/sb-types';

import { getLocalePrefix, replaceTemplate } from '../utils';

type Messages = Record<string, any>;

const IntlContext = createContext<{
  messages: Messages | null;
  locale: Locales;
}>({
  messages: null,
  locale: 'en',
});
export const IntlProvider = ({
  children,
  messages,
  locale,
}: {
  children: ReactNode;
  messages: Messages | null;
  locale: Locales;
}) => {
  const value = useMemo(
    () => ({
      messages,
      locale,
    }),
    [locale, messages]
  );
  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
};

type RichTranslationValues = Record<
  string,
  ({ chunks }: { chunks: ReactNode }) => ReactNode
>;

const richRegex = /<(\w+)>(.*?)<\/\1>/g;
export function useTranslations<T>(messages: T) {
  const { locale } = useContext(IntlContext);

  /**
   * Memoized translation function.
   * @returns {Function} The translation function.
   */
  const translate = useMemo(() => {
    /**
     * Retrieves the nested value from the messages object based on the provided key.
     * @param {string} key - The key to retrieve the value for.
     * @param {messages} messages - The messages object.
     * @param {replacements} replacements - text replacement fragment.
     * @returns {any} The nested value.
     */
    function getNestedValue(
      key: string,
      messages: Messages,
      replacements?: { [k: string]: any }
    ) {
      try {
        const keys = key.split('.') as string[];
        // Direct key lookup
        if (key in messages) {
          let message = messages[key];
          if (replacements) {
            const replacedMessage = replaceTemplate(
              messages[key],
              replacements
            );
            message = replacedMessage as T;
          }
          return message;
        }

        let value = messages;
        for (const k of keys) {
          if (
            value === null ||
            value === undefined ||
            typeof value !== 'object' ||
            !(k in value)
          ) {
            console.error(`Translation error for key "${key}"`);
          }
          value = value[k];
          if (!value && value !== '' && value !== 0 && value !== false) break;
        }

        if (replacements && typeof value === 'string') {
          return replaceTemplate(value, replacements);
        }

        return value;
      } catch (error) {
        throw new Error(`Translation error for key "${key}": error`);
      }
    }

    /**
     * Translates the given key.
     * @param {string} key - The key to translate.
     * @param {replacements} replacements - text replacement fragment.
     * @returns {any} The translated value.
     */
    function t(key: string, replacements?: { [k: string]: any }) {
      if (!messages) return '';
      return getNestedValue(key, messages, replacements);
    }

    /**
     * Translates the given key with rich text support.
     * @param {string} key - The key to translate.
     * @param {RichTranslationValues} components - The components to use for rich text rendering.
     * @returns {ReactNode[]} The translated value with rich text components.
     */
    t.rich = function rich(key: string, components: RichTranslationValues) {
      const str = t(key);

      if (!str) return null;

      const matchRes = str.matchAll(richRegex);
      const matchResArr = [...matchRes];

      const result: ReactNode[] = [str];

      // Replace the matched rich text tags with corresponding React components
      matchResArr.forEach((matchedGroups, index) => {
        const [match, tag, content] = matchedGroups;
        const { index: matchIdx } = matchedGroups;
        const mLength = match?.length;

        // If the tag or match length is missing, skip this iteration
        if (!tag || !mLength) return;

        const resLastIdx = result.length - 1;
        const stringToReplace = result[resLastIdx];

        const Component = components[tag];

        // If the string to replace is not a string or the component is missing, skip this iteration
        if (typeof stringToReplace !== 'string' || !Component || !matchIdx)
          return;

        const lengthDiff = str.length - stringToReplace.length;
        const shiftedMatchIdx = matchIdx - lengthDiff;

        // Split the string to replace into two parts: before and after the matched tag
        const first = stringToReplace.slice(0, shiftedMatchIdx);
        const rest = stringToReplace.slice(shiftedMatchIdx + mLength);

        // Create a React component with the provided content as chunks
        const node = <Component key={index} chunks={content} />;

        // Replace the string to replace with the first part
        result[resLastIdx] = first;

        // Add the React component and the rest of the string to the result array
        result.push(...[node, rest]);
      });

      return result;
    };

    return t;
  }, [locale, messages]);

  return translate;
}

export const useLocale = <T = Locales,>() => {
  const { locale } = useContext(IntlContext);
  return locale as T;
};

export const usePathname = <T extends Locales>() => {
  const locale = useLocale<T>();
  const fullPathname = useFullPathname() || '/';

  const localePrefix = getLocalePrefix(locale) + '/';
  return fullPathname.replace(localePrefix, '/');
};
